import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, Mail, CheckCircle, Star, Store, X, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FASH_LOGOS, FASH_LOGO_LIGHT } from '../constants/brand';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

type WaitlistRole = 'shopper' | 'retailer';

function WaitlistModal({
  isOpen,
  onClose,
  initialEmail = '',
}: {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
}) {
  const [role, setRole] = useState<WaitlistRole | ''>('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [city, setCity] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setEmail(initialEmail);
  }, [isOpen, initialEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!role) {
      setError('Choose shopper or retailer.');
      return;
    }
    if (!name.trim() || !email.trim() || !city.trim()) return;
    if (role === 'retailer' && !businessName.trim()) return;

    setLoading(true);
    setError('');
    try {
      const joinWaitlist = httpsCallable(functions, 'joinWaitlist');
      await joinWaitlist({
        role,
        name: name.trim(),
        email: email.trim(),
        city: city.trim(),
        businessName: role === 'retailer' ? businessName.trim() : '',
      });
      setSubmitted(true);
    } catch (err: any) {
      console.error('Waitlist signup failed:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setRole('');
    setName('');
    setEmail('');
    setCity('');
    setBusinessName('');
    setError('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleReset}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="relative w-full max-w-md max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
          >
            <div className="relative bg-gradient-to-r from-primary to-primary-hover p-6 pb-8">
              <button
                onClick={handleReset}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white"
              >
                <X className="w-4 h-4" />
              </button>
              <Mail className="w-8 h-8 text-white mb-3" />
              <h3 className="text-xl font-serif text-white">Join the Waitlist</h3>
              <p className="text-white/80 text-sm font-sans mt-1">
                {role === 'retailer'
                  ? 'Tell us about your shop and we will review your request.'
                  : 'Tell us who you are and we will review your request.'}
              </p>
            </div>

            <div className="p-6 -mt-3">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center gap-3 py-6"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    >
                      <CheckCircle className="w-16 h-16 text-success" />
                    </motion.div>
                    <h3 className="text-xl font-serif text-heading">You're on the list!</h3>
                    <p className="text-body font-sans text-sm text-center">
                      We'll email you when you're approved.
                    </p>
                    <button
                      onClick={handleReset}
                      className="mt-4 text-sm text-primary font-sans font-medium"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <p className="block text-sm font-sans font-medium text-heading mb-1.5">
                        I am a <span className="text-error">*</span>
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setRole('shopper')}
                          className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                            role === 'shopper'
                              ? 'border-primary bg-purple-50 text-heading'
                              : 'border-gray-200 text-body hover:border-gray-300'
                          }`}
                        >
                          <Sparkles className="w-4 h-4 mb-1" />
                          <span className="block text-sm font-medium">Shopper</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setRole('retailer')}
                          className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                            role === 'retailer'
                              ? 'border-primary bg-purple-50 text-heading'
                              : 'border-gray-200 text-body hover:border-gray-300'
                          }`}
                        >
                          <Store className="w-4 h-4 mb-1" />
                          <span className="block text-sm font-medium">Retailer / Shop</span>
                        </button>
                      </div>
                    </div>

                    {role && (
                      <>
                        <div>
                          <label className="block text-sm font-sans font-medium text-heading mb-1.5">
                            Your name <span className="text-error">*</span>
                          </label>
                          <Input
                            type="text"
                            placeholder="Full name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="h-12 text-base"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-sans font-medium text-heading mb-1.5">
                            Email <span className="text-error">*</span>
                          </label>
                          <Input
                            type="email"
                            placeholder={role === 'retailer' ? 'you@shop.com' : 'Enter your email address'}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="h-12 text-base"
                          />
                        </div>

                        {role === 'retailer' && (
                          <div>
                            <label className="block text-sm font-sans font-medium text-heading mb-1.5">
                              Shop name <span className="text-error">*</span>
                            </label>
                            <Input
                              type="text"
                              placeholder="Your boutique or brand"
                              value={businessName}
                              onChange={(e) => setBusinessName(e.target.value)}
                              required
                              className="h-12 text-base"
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-sans font-medium text-heading mb-1.5">
                            City <span className="text-error">*</span>
                          </label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              type="text"
                              placeholder="City, State"
                              value={city}
                              onChange={(e) => setCity(e.target.value)}
                              required
                              className="h-12 text-base pl-10"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {error && (
                      <p className="text-sm text-error font-sans text-center">{error}</p>
                    )}

                    <Button type="submit" size="lg" fullWidth className="mt-2" disabled={loading}>
                      {loading ? (
                        <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Joining...</>
                      ) : (
                        <>Get Early Access <ArrowRight className="ml-2 w-4 h-4" /></>
                      )}
                    </Button>

                    <p className="text-xs text-gray-400 font-sans text-center mt-3">
                      By joining, you agree to receive marketing emails. Unsubscribe anytime.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SignInEmailModal({
  isOpen,
  onClose,
  onAllowed,
  onNeedsWaitlist,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAllowed: (email: string) => void;
  onNeedsWaitlist: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setLoading(true);
    setError('');
    try {
      const checkSignInAccess = httpsCallable(functions, 'checkSignInAccess');
      const result = await checkSignInAccess({ email: trimmed });
      const allowed = (result.data as { allowed?: boolean })?.allowed === true;
      setLoading(false);
      if (allowed) onAllowed(trimmed);
      else onNeedsWaitlist(trimmed);
      return;
    } catch (err: any) {
      console.error('Sign-in access check failed:', err);
      setError(err?.message || 'Could not check this email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="relative bg-gradient-to-r from-primary to-primary-hover p-6 pb-8">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white"
              >
                <X className="w-4 h-4" />
              </button>
              <h3 className="text-xl font-serif text-white">Sign in</h3>
              <p className="text-white/80 text-sm font-sans mt-1">
                Enter the email on your FASH account.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 -mt-3 space-y-4">
              <div>
                <label className="block text-sm font-sans font-medium text-heading mb-1.5">
                  Email
                </label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 text-base"
                  autoFocus
                />
              </div>
              {error && <p className="text-sm text-error font-sans text-center">{error}</p>}
              <Button type="submit" size="lg" fullWidth disabled={loading}>
                {loading ? (
                  <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Checking...</>
                ) : (
                  <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>
                )}
              </Button>
            </form>
          </div>
    </div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');

  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.25], [1, 0.95]);

  useEffect(() => {
    const state = location.state as { openWaitlist?: boolean; email?: string } | null;
    if (!state?.openWaitlist) return;
    setWaitlistEmail(state.email || '');
    setShowWaitlistModal(true);
    setShowSignInModal(false);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const openWaitlist = (email = '') => {
    setWaitlistEmail(email);
    setShowSignInModal(false);
    setShowWaitlistModal(true);
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
        initialEmail={waitlistEmail}
      />
      <SignInEmailModal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        onAllowed={(email) => {
          setShowSignInModal(false);
          navigate('/signin', { state: { email, accessGranted: true } });
        }}
        onNeedsWaitlist={(email) => openWaitlist(email)}
      />

      {/* ── Floating Nav ── */}
      <motion.nav
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/70 border-b border-gray-100"
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center">
          <img src={FASH_LOGOS.purple} alt="FASH" className="h-28 sm:h-32 object-contain" />
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => setShowSignInModal(true)}
          >
            Sign In
          </Button>
        </div>
      </motion.nav>

      {/* ── Hero Section ── */}
      <motion.section
        style={{ opacity: heroOpacity, scale: heroScale }}
        className="relative min-h-screen flex items-center justify-center pt-20"
      >
        {/* Animated gradient background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              background: [
                'radial-gradient(ellipse at 20% 50%, #F3E8FF 0%, transparent 50%)',
                'radial-gradient(ellipse at 80% 50%, #F3E8FF 0%, transparent 50%)',
                'radial-gradient(ellipse at 50% 20%, #F3E8FF 0%, transparent 50%)',
                'radial-gradient(ellipse at 20% 50%, #F3E8FF 0%, transparent 50%)',
              ],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0"
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,white_70%)]" />
        </div>

        {/* Floating decorative elements */}
        <motion.div
          animate={{ y: [-20, 20, -20], rotate: [0, 5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-32 left-[10%] w-20 h-20 rounded-full bg-primary/10 blur-xl"
        />
        <motion.div
          animate={{ y: [15, -15, 15], rotate: [0, -5, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-40 right-[15%] w-32 h-32 rounded-full bg-secondary blur-2xl"
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.div variants={fadeUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-primary-hover text-sm font-sans font-medium">
                <Sparkles className="w-4 h-4" />
                Coming Soon
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif text-heading leading-[1.05] tracking-tight mb-6"
            >
              Fashion <span className="text-heading italic">Delivered</span>
              <br />
              <span className="text-primary-hover italic uppercase">same day</span>
              <br />
              to your door.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-lg sm:text-xl text-body font-sans max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              Get early access to exclusive discounts, special offers, and free delivery
              when you join our mailing list. Be the first to shop curated fashion from
              local boutiques — delivered same day.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="text-base px-8" onClick={() => openWaitlist()}>
                Join the Waitlist
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base px-8"
                onClick={() => setShowSignInModal(true)}
              >
                Sign In
              </Button>
            </motion.div>
          </motion.div>

        </div>
      </motion.section>

      {/* ── Social Proof / Teaser ── */}
      <section className="py-16 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="flex flex-col items-center text-center"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-primary text-primary" />
              ))}
            </motion.div>
            <motion.p variants={fadeUp} className="text-lg font-serif text-heading italic max-w-xl mb-2">
              "Finally, a fashion marketplace that brings local boutiques to my doorstep — same day."
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-12 bg-heading">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center gap-6">
          <img src={FASH_LOGO_LIGHT} alt="FASH" className="h-8 object-contain opacity-80" />
          <p className="text-white/50 text-sm font-sans text-center">
            &copy; {new Date().getFullYear()} FASH. All rights reserved. Coming soon to your city.
          </p>
        </div>
      </footer>
    </div>
  );
}
